import React, { useState, useEffect } from "react";
import api from "@/services/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Truck,
  Plus,
  Minus,
  Clock,
  CheckCircle,
  XCircle,
  Package,
  MapPin,
  Calendar,
  Send,
} from "lucide-react";

export default function VendedorRequisiciones() {
  const [requisiciones, setRequisiciones] = useState([]);
  const [productos, setProductos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [cart, setCart] = useState([]);
  const [form, setForm] = useState({
    direccion_entrega: "",
    fecha_requerida: "",
    notas: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [reqRes, prodRes] = await Promise.all([
        api.get("/requisiciones"),
        api.get("/productos"),
      ]);
      setRequisiciones(reqRes.data);
      setProductos(prodRes.data.filter((p) => p.activo));
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const addToCart = (producto) => {
    const existing = cart.find((c) => c.producto_id === producto.id);
    if (existing) {
      setCart(
        cart.map((c) =>
          c.producto_id === producto.id
            ? { ...c, cantidad: c.cantidad + 1 }
            : c
        )
      );
    } else {
      setCart([
        ...cart,
        {
          producto_id: producto.id,
          producto_nombre: producto.nombre,
          cantidad: 1,
        },
      ]);
    }
  };

  const updateQuantity = (productoId, delta) => {
    setCart(
      cart
        .map((c) => {
          if (c.producto_id === productoId) {
            const newQty = Math.max(0, c.cantidad + delta);
            return newQty === 0 ? null : { ...c, cantidad: newQty };
          }
          return c;
        })
        .filter(Boolean)
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (cart.length === 0) {
      toast.error("Agrega productos a la requisición");
      return;
    }
    if (!form.direccion_entrega || !form.fecha_requerida) {
      toast.error("Completa la dirección y fecha de entrega");
      return;
    }

    setSubmitting(true);
    try {
      await api.post("/requisiciones", {
        items: cart,
        direccion_entrega: form.direccion_entrega,
        fecha_requerida: form.fecha_requerida,
        notas: form.notas,
      });
      toast.success("Requisición enviada");
      setDialogOpen(false);
      setCart([]);
      setForm({ direccion_entrega: "", fecha_requerida: "", notas: "" });
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail || error.message || "Error desconocido";
      toast.error(`Error al crear requisición: ${detail}`);
      console.error("Error creating requisition:", error.response?.data || error);
    } finally {
      setSubmitting(false);
    }
  };

  const getEstadoIcon = (estado) => {
    switch (estado) {
      case "pendiente":
        return <Clock className="w-5 h-5 text-amber-500" />;
      case "aprobada":
        return <CheckCircle className="w-5 h-5 text-blue-500" />;
      case "despachada":
        return <Truck className="w-5 h-5 text-green-500" />;
      case "rechazada":
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <Clock className="w-5 h-5 text-gray-500" />;
    }
  };

  const getEstadoColor = (estado) => {
    switch (estado) {
      case "pendiente":
        return "bg-amber-100 text-amber-700";
      case "aprobada":
        return "bg-blue-100 text-blue-700";
      case "despachada":
        return "bg-green-100 text-green-700";
      case "rechazada":
        return "bg-red-100 text-red-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-torta-brown">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="vendedor-requisiciones">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl text-torta-dark">Requisiciones</h1>
          <p className="text-torta-muted">Solicita inventario a bodega</p>
        </div>
        <Button
          onClick={() => setDialogOpen(true)}
          className="bg-torta-brown hover:bg-torta-brown/90"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nueva Requisición
        </Button>
      </div>

      {/* Lista de Requisiciones */}
      <div className="space-y-4">
        {requisiciones.length === 0 ? (
          <div className="bg-white rounded-2xl p-8 text-center">
            <Truck className="w-12 h-12 text-torta-muted mx-auto mb-4" />
            <p className="text-torta-muted">No tienes requisiciones</p>
            <p className="text-sm text-torta-muted mt-1">
              Crea una para solicitar inventario
            </p>
          </div>
        ) : (
          requisiciones.map((req) => (
            <div key={req.id} className="bg-white rounded-2xl p-5 shadow-sm">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  {getEstadoIcon(req.estado)}
                  <div>
                    <p className="font-medium text-torta-dark">
                      Requisición #{req.id.slice(0, 8)}
                    </p>
                    <p className="text-xs text-torta-muted">
                      {new Date(req.fecha_creacion).toLocaleString("es-CO")}
                    </p>
                  </div>
                </div>
                <span
                  className={`px-3 py-1 rounded-full text-xs font-medium ${getEstadoColor(
                    req.estado
                  )}`}
                >
                  {req.estado.toUpperCase()}
                </span>
              </div>

              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-torta-muted mt-1" />
                  <div>
                    <p className="text-xs text-torta-muted">Dirección de entrega</p>
                    <p className="text-sm text-torta-dark">{req.direccion_entrega}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Calendar className="w-4 h-4 text-torta-muted mt-1" />
                  <div>
                    <p className="text-xs text-torta-muted">Fecha requerida</p>
                    <p className="text-sm text-torta-dark">{req.fecha_requerida}</p>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 mb-3">
                {req.items?.map((item, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center gap-1 bg-torta-cream px-3 py-1 rounded-full text-sm"
                  >
                    <Package className="w-3 h-3" />
                    {item.cantidad}x {item.producto_nombre}
                  </span>
                ))}
              </div>

              {req.notas && (
                <p className="text-sm text-torta-muted bg-torta-cream/50 rounded-lg p-2">
                  <strong>Notas:</strong> {req.notas}
                </p>
              )}

              {req.notas_admin && (
                <p className="text-sm text-blue-700 bg-blue-50 rounded-lg p-2 mt-2">
                  <strong>Respuesta admin:</strong> {req.notas_admin}
                </p>
              )}
            </div>
          ))
        )}
      </div>

      {/* Dialog Nueva Requisición */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <Truck className="w-6 h-6 text-torta-brown" />
              Nueva Requisición de Inventario
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            {/* Productos */}
            <div>
              <Label className="mb-2 block">Selecciona productos</Label>
              <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                {productos.map((p) => {
                  const inCart = cart.find((c) => c.producto_id === p.id);
                  return (
                    <div
                      key={p.id}
                      className={`p-3 rounded-xl border ${
                        inCart
                          ? "border-torta-brown bg-torta-cream/50"
                          : "border-torta-sand"
                      }`}
                    >
                      <p className="text-sm font-medium text-torta-dark">{p.nombre}</p>
                      <p className="text-xs text-torta-muted mb-2">
                        Stock: {p.stock_central || 0}
                      </p>
                      {inCart ? (
                        <div className="flex items-center gap-2">
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => updateQuantity(p.id, -1)}
                            className="w-7 h-7 p-0"
                          >
                            <Minus className="w-3 h-3" />
                          </Button>
                          <span className="w-8 text-center text-sm font-medium">
                            {inCart.cantidad}
                          </span>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => updateQuantity(p.id, 1)}
                            className="w-7 h-7 p-0"
                          >
                            <Plus className="w-3 h-3" />
                          </Button>
                        </div>
                      ) : (
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => addToCart(p)}
                          className="w-full bg-torta-brown hover:bg-torta-brown/90"
                        >
                          <Plus className="w-3 h-3 mr-1" />
                          Agregar
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Resumen */}
            {cart.length > 0 && (
              <div className="bg-torta-cream rounded-xl p-3">
                <p className="text-sm font-medium text-torta-dark mb-2">
                  Productos seleccionados:
                </p>
                <div className="flex flex-wrap gap-2">
                  {cart.map((item) => (
                    <span
                      key={item.producto_id}
                      className="bg-white px-2 py-1 rounded text-xs"
                    >
                      {item.cantidad}x {item.producto_nombre}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Dirección */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-torta-brown" />
                Dirección de entrega *
              </Label>
              <Input
                required
                value={form.direccion_entrega}
                onChange={(e) =>
                  setForm({ ...form, direccion_entrega: e.target.value })
                }
                placeholder="Calle, número, barrio, ciudad"
              />
            </div>

            {/* Fecha */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-torta-brown" />
                Fecha requerida de entrega *
              </Label>
              <Input
                type="date"
                required
                value={form.fecha_requerida}
                onChange={(e) =>
                  setForm({ ...form, fecha_requerida: e.target.value })
                }
                min={new Date().toISOString().split("T")[0]}
              />
            </div>

            {/* Notas */}
            <div className="space-y-2">
              <Label>Notas adicionales (opcional)</Label>
              <Textarea
                value={form.notas}
                onChange={(e) => setForm({ ...form, notas: e.target.value })}
                placeholder="Indicaciones especiales, horario preferido, etc."
                rows={2}
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={cart.length === 0 || submitting}
                className="flex-1 bg-torta-brown hover:bg-torta-brown/90"
              >
                {submitting ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Enviando...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Send className="w-4 h-4" />
                    Enviar Requisición
                  </span>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
